import type { HWPXDocument, ReadOptions, DocumentMeta, BinDataItem } from '../../types.js';
import { extractZip, decodeUtf8 } from './zip.js';
import { parseHeaderXml } from './xml/parse-header.js';
import { parseSectionXml } from './xml/parse-section.js';
import { parseContentHpfXml } from './xml/parse-content-hpf.js';
import { HWPXError } from '../../errors.js';

export function readHwpx(data: Uint8Array, _opts?: ReadOptions): HWPXDocument {
  const files = extractZip(data);

  // mimetype 검증
  const mimetype = files['mimetype'];
  if (mimetype) {
    const mt = decodeUtf8(mimetype);
    if (!mt.includes('hwp')) {
      throw new HWPXError(`Invalid mimetype: "${mt}"`);
    }
  }

  // content.hpf 파싱 → 섹션 파일 목록
  const contentHpf = getFile(files, 'Contents/content.hpf');
  const hpfInfo = parseContentHpfXml(decodeUtf8(contentHpf));

  // header.xml 파싱
  const headerXml = getFile(files, 'Contents/header.xml');
  const head = parseHeaderXml(decodeUtf8(headerXml));

  // sections 파싱
  const sections = hpfInfo.sectionFiles.map((file) => {
    const path = file.startsWith('Contents/') ? file : `Contents/${file}`;
    const sectionData = getFile(files, path);
    return parseSectionXml(decodeUtf8(sectionData));
  });

  // meta 구성
  const meta = parseMeta(files, hpfInfo.title);

  // binData 구성
  const binData = parseBinData(files, hpfInfo.binDataFiles);

  return { meta, head, sections, binData };
}

function getFile(files: Record<string, Uint8Array>, path: string): Uint8Array {
  const data = files[path];
  if (!data) {
    throw new HWPXError(`Required file not found in HWPX archive: ${path}`);
  }
  return data;
}

function parseMeta(
  files: Record<string, Uint8Array>,
  title: string,
): DocumentMeta {
  const meta: DocumentMeta = {
    hwpVersion: '5.1.0.1',
    title: title || undefined,
  };

  // version.xml에서 버전 정보 추출
  const versionData = files['version.xml'];
  if (versionData) {
    const versionXml = decodeUtf8(versionData);
    const versionMatch = versionXml.match(/<ha:appversion[^>]*>([^<]+)/);
    if (versionMatch) {
      meta.hwpVersion = versionMatch[1];
    }
  }

  return meta;
}

function parseBinData(
  files: Record<string, Uint8Array>,
  binDataFiles: { id: string; href: string; mediaType: string }[],
): BinDataItem[] {
  const items: BinDataItem[] = [];

  for (let i = 0; i < binDataFiles.length; i++) {
    const { href, mediaType } = binDataFiles[i];
    // href는 상대경로(../BinData/xxx) 또는 절대경로
    const cleanPath = href.replace(/^\.\.\//, '');
    const path = cleanPath.startsWith('BinData/') ? cleanPath : `BinData/${cleanPath}`;
    const data = files[path];
    if (!data) continue;

    const name = path.replace('BinData/', '');
    const format = mediaTypeToFormat(mediaType);

    items.push({
      id: i + 1,
      format,
      name,
      data,
    });
  }

  return items;
}

function mediaTypeToFormat(mediaType: string): BinDataItem['format'] {
  const map: Record<string, BinDataItem['format']> = {
    'image/jpeg': 'jpg',
    'image/png': 'png',
    'image/gif': 'gif',
    'image/bmp': 'bmp',
    'image/tiff': 'tiff',
    'image/x-wmf': 'wmf',
    'image/x-emf': 'emf',
  };
  return map[mediaType] || 'png';
}
